// =============================================================================
// T-442 (AC-8) — « Offre d'aides » : la campagne envoyée par lots depuis OmniHub, les réponses classées.
//   1. le texte (offre + relance) : écrit, relu, validé UNE fois ;  2. qui la recevrait (tous les clients hors dossiers annulés, une fiche par adresse) ;
//   3. préparer les fiches ;  4. envoyer par lots de 50 ;  5. relancer UNE fois à J+10 ;  6. les réponses classées et les compteurs.
// Aucune décision ici : le serveur applique la garde commerciale, le plafond et le mode (essai / réel). Rien ne part sans un clic.
// Le texte ne cite aucune aide ni aucun montant (refusé par le serveur). Charte : jetons var(--oh-*) uniquement, lisible à 390 px.
// =============================================================================
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { OhField, OhInput, OhStatusNote } from "../../../components/ui/oh-charte";
import Bouton from "./boutonBureau";
import OffreAidesLot from "./OffreAidesLot";
import { offreAides } from "./bureauAmoApi";

const muted = { color: "var(--oh-text-muted)", fontSize: 14, lineHeight: 1.6 };
const carte = { padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)" };
const zone = { width: "100%", minHeight: 140, padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", border: "1px solid var(--oh-border)", background: "var(--oh-bg-surface)", color: "var(--oh-text)", font: "inherit", lineHeight: 1.6 };
const titre = { margin: "0 0 var(--oh-space-2)", fontSize: 16, fontWeight: 500, color: "var(--oh-text)" };

export const BROUILLON = {
  nom: "Offre d'aides — premier envoi",
  objet: "Une information sur les aides pour votre logement",
  texte: "Bonjour {nom},\n\nNous vous accompagnons déjà pour votre dossier. Nous pouvons aussi regarder avec vous s'il existe d'autres aides pour votre situation.\n\nSi cela vous intéresse, un clic suffit pour être rappelé : {lien}\n\nCordialement,\nL'équipe",
  objet_relance: "Rappel : une information sur les aides pour votre logement",
  texte_relance: "Bonjour {nom},\n\nNous revenons vers vous une seule fois au sujet de notre message précédent. Si vous souhaitez être rappelé : {lien}\n\nCordialement,\nL'équipe",
};
export const RAISONS = {
  oppose: "opposés à la prospection", sans_email_valide: "sans adresse e-mail valide", adresse_invalide: "adresses qui ont rebondi",
  prestation_ouverte: "prestation déjà ouverte", deja_sollicite: "déjà sollicités", annule: "dossiers annulés", hors_selection: "hors sélection",
};
const date = (iso) => { if (!iso) return ""; const d = new Date(iso); return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toLocaleDateString("fr-FR"); };
const msg = (e) => (typeof e?.detail === "string" ? e.detail : "Action impossible, réessayez.");

function Compteur({ id, libelle, valeur }) {
  return (
    <div data-testid={`offre-compteur-${id}`} style={{ ...carte, padding: "var(--oh-space-3)", minWidth: 0 }}>
      <span style={{ display: "block", fontSize: 28, fontWeight: 500, color: "var(--oh-text)", lineHeight: 1.2 }} data-testid={`offre-compteur-${id}-valeur`}>{valeur ?? "…"}</span>
      <span style={{ ...muted, fontSize: 13 }}>{libelle}</span>
    </div>
  );
}

export default function OffreAides() {
  const [campagnes, setCampagnes] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(BROUILLON);
  const [creation, setCreation] = useState(false);
  const [compteurs, setCompteurs] = useState(null);
  const [cible, setCible] = useState(null);
  const [reponses, setReponses] = useState([]);
  const [filtre, setFiltre] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [bilan, setBilan] = useState(null);

  const campagne = campagnes?.find((c) => c.id === sel) || null;

  const charger = useCallback(async (idChoisi) => {
    try {
      const d = await offreAides.campagnes();
      setCampagnes(d.campagnes); setClasses(d.classes || []);
      const id = idChoisi || d.campagnes[0]?.id || null;
      setSel(id);
      setCreation(d.campagnes.length === 0);
      setCible(await offreAides.cible());
      if (id) {
        const c = d.campagnes.find((x) => x.id === id);
        if (c?.statut === "brouillon") setForm({ nom: c.nom, objet: c.objet, texte: c.texte, objet_relance: c.objet_relance, texte_relance: c.texte_relance });
        setCompteurs(await offreAides.compteurs(id));
        setReponses((await offreAides.reponses({ campagne_id: id })).reponses);
      } else { setCompteurs(null); setReponses([]); }
    } catch (e) { toast.error(msg(e)); setCampagnes([]); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const faire = async (f, ok) => {
    setOccupe(true);
    try { const r = await f(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); return r; }
    catch (e) { toast.error(msg(e)); return null; }
    finally { setOccupe(false); }
  };

  const creer = async () => { const c = await faire(() => offreAides.creer(form), "Campagne créée : relisez le texte, puis validez-le."); if (c) { setBilan(null); await charger(c.id); } };
  const enregistrer = async () => { const c = await faire(() => offreAides.modifier(campagne.id, { version: campagne.version, ...form }), "Texte enregistré."); if (c) await charger(c.id); };
  const valider = async () => { const c = await faire(() => offreAides.validerTexte(campagne.id, campagne.version, campagne.sha256), "Texte validé. Il ne changera plus."); if (c) await charger(c.id); };
  const preparer = async (ids) => {
    const r = await faire(() => offreAides.proposer(campagne.id, ids), (x) => `${x.creees} fiche${x.creees > 1 ? "s" : ""} préparée${x.creees > 1 ? "s" : ""}. Rien n'est parti.`);
    if (r) { setBilan(`${r.creees} préparée(s), ${r.deja_preparees} déjà prête(s), dont ${r.adresses_partagees} adresse(s) partagée(s).`); await charger(campagne.id); }
  };
  const relancer = async () => {
    const r = await faire(() => offreAides.relances(campagne.id), (x) => `${x.creees} relance${x.creees > 1 ? "s" : ""} à valider dans le Bureau.`);
    if (r) { setBilan(`Relances : ${r.creees} créée(s), ${r.deja} déjà créée(s), ${r.avec_reponse} avec une réponse, ${r.ecartees} écartée(s).`); await charger(campagne.id); }
  };
  const requalifier = async (id, classe) => { if (await faire(() => offreAides.requalifier(id, classe), "Réponse requalifiée.")) await charger(campagne.id); };

  if (campagnes === null) return <p style={muted} data-testid="offre-chargement">Chargement de l'offre d'aides…</p>;
  const brouillon = campagne?.statut === "brouillon";
  const valide = campagne?.statut === "texte_valide";
  const affichees = filtre ? reponses.filter((r) => r.classe === filtre) : reponses;
  const cinquante = (cible?.adresses || []).slice(0, 50).flatMap((a) => a.client_ids);

  return (
    <div data-testid="offre-aides" className="oh-bm-racine" style={{ display: "grid", gap: "var(--oh-space-4)" }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 500, color: "var(--oh-text)" }}>Offre d'aides</h1>
        <p style={{ ...muted, margin: "4px 0 0" }}>Vous écrivez le texte, vous le validez une seule fois, puis les offres partent par lots de 50, un clic par lot. Les réponses sont classées ici.</p>
      </header>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Campagnes">
        {campagnes.map((c) => (
          <Bouton key={c.id} type={c.id === sel && !creation ? "secondaire" : "discret"} aria-pressed={c.id === sel && !creation} data-testid={`offre-campagne-${c.id}`}
            onClick={() => { setCreation(false); setBilan(null); charger(c.id); }}>{c.nom}</Bouton>
        ))}
        <Bouton type="discret" data-testid="offre-nouvelle" onClick={() => { setCreation(true); setForm(BROUILLON); }}>Nouvelle campagne</Bouton>
      </div>

      {(creation || brouillon) && (
        <section style={carte} data-testid="offre-texte" aria-label="Le texte">
          <h2 style={titre}>{creation ? "Nouvelle campagne" : "Le texte (brouillon)"}</h2>
          <p style={{ ...muted, margin: "0 0 8px" }}>{"Écrivez {nom} pour le nom du client et {lien} pour le lien « Oui, je suis intéressé » (obligatoire). Aucun montant, aucune aide précise : le serveur les refuse."}</p>
          <div style={{ display: "grid", gap: "var(--oh-space-3)" }}>
            <OhField label="Nom de la campagne" htmlFor="offre-nom"><OhInput id="offre-nom" data-testid="offre-champ-nom" value={form.nom} maxLength={120} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></OhField>
            <OhField label="Objet du mail" htmlFor="offre-objet"><OhInput id="offre-objet" data-testid="offre-champ-objet" value={form.objet} maxLength={200} onChange={(e) => setForm({ ...form, objet: e.target.value })} /></OhField>
            <OhField label="Texte du mail" htmlFor="offre-texte"><textarea id="offre-texte" data-testid="offre-champ-texte" style={zone} value={form.texte} maxLength={4000} onChange={(e) => setForm({ ...form, texte: e.target.value })} /></OhField>
            <OhField label="Objet de la relance (une seule, dix jours après)" htmlFor="offre-objet-relance"><OhInput id="offre-objet-relance" data-testid="offre-champ-objet-relance" value={form.objet_relance} maxLength={200} onChange={(e) => setForm({ ...form, objet_relance: e.target.value })} /></OhField>
            <OhField label="Texte de la relance" htmlFor="offre-texte-relance"><textarea id="offre-texte-relance" data-testid="offre-champ-texte-relance" style={zone} value={form.texte_relance} maxLength={4000} onChange={(e) => setForm({ ...form, texte_relance: e.target.value })} /></OhField>
          </div>
          <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 12 }}>
            {creation ? (
              <Bouton type="primaire" disabled={occupe} data-testid="offre-creer" onClick={creer}>Créer la campagne</Bouton>
            ) : (
              <>
                <Bouton type="secondaire" disabled={occupe} data-testid="offre-enregistrer" onClick={enregistrer}>Enregistrer le texte</Bouton>
                <Bouton type="primaire" disabled={occupe} data-testid="offre-valider" onClick={valider}>Valider ce texte (une seule fois)</Bouton>
              </>
            )}
          </div>
          {brouillon && <p style={{ ...muted, margin: "8px 0 0" }}>Pensez à enregistrer avant de valider : la validation porte sur le texte enregistré (empreinte {campagne.sha256.slice(0, 8)}).</p>}
        </section>
      )}

      {valide && !creation && (
        <section style={carte} data-testid="offre-valide" aria-label="Texte validé">
          <h2 style={titre}>Texte validé</h2>
          <p style={{ ...muted, margin: 0 }} data-testid="offre-valide-phrase">Validé le {date(campagne.valide_le)} · empreinte {campagne.sha256.slice(0, 8)} · il ne change plus.</p>
          <p style={{ margin: "8px 0 0", color: "var(--oh-text)", fontWeight: 500 }}>{campagne.objet}</p>
          <pre style={{ whiteSpace: "pre-wrap", margin: "4px 0 0", font: "inherit", color: "var(--oh-text)", fontSize: 14 }}>{campagne.texte}</pre>
          <details style={{ marginTop: 8 }}><summary style={{ cursor: "pointer", color: "var(--oh-accent-text)", fontSize: 14 }}>Voir la relance</summary>
            <p style={{ margin: "8px 0 0", color: "var(--oh-text)", fontWeight: 500 }}>{campagne.objet_relance}</p>
            <pre style={{ whiteSpace: "pre-wrap", margin: "4px 0 0", font: "inherit", color: "var(--oh-text)", fontSize: 14 }}>{campagne.texte_relance}</pre>
          </details>
        </section>
      )}

      {campagne && !creation && compteurs && (
        <section aria-label="Compteurs" data-testid="offre-compteurs" style={{ display: "grid", gap: "var(--oh-space-3)", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}>
          <Compteur id="proposees" libelle="Proposées" valeur={compteurs.proposees} />
          <Compteur id="envoyees" libelle="Envoyées" valeur={compteurs.envoyees} />
          <Compteur id="reponses" libelle="Réponses" valeur={compteurs.reponses} />
          <Compteur id="interesses" libelle="Intéressés" valeur={compteurs.interesses} />
          <Compteur id="prestations" libelle="Prestations ouvertes" valeur={compteurs.prestations_ouvertes} />
        </section>
      )}

      {valide && !creation && (
        <>
          <section style={carte} data-testid="offre-cible" aria-label="Qui recevrait l'offre">
            <h2 style={titre}>Qui recevrait l'offre</h2>
            {cible && (
              <>
                <p style={{ margin: 0, color: "var(--oh-text)" }} data-testid="offre-cible-phrase">
                  {cible.nb_adresses} adresse{cible.nb_adresses > 1 ? "s" : ""} pour {cible.nb_clients} client{cible.nb_clients > 1 ? "s" : ""}
                  {cible.adresses_partagees > 0 ? `, dont ${cible.adresses_partagees} adresse${cible.adresses_partagees > 1 ? "s" : ""} partagée${cible.adresses_partagees > 1 ? "s" : ""} (une seule fiche)` : ""}.
                </p>
                <p style={{ ...muted, margin: "4px 0 0" }} data-testid="offre-cible-exclus">
                  Écartés : {Object.entries(cible.exclus).filter(([k, v]) => v > 0 && k !== "hors_selection").map(([k, v]) => `${v} ${RAISONS[k] || k}`).join(" · ") || "personne"}.
                </p>
                <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 12 }}>
                  <Bouton type="secondaire" disabled={occupe || cible.nb_adresses === 0} data-testid="offre-preparer-50" onClick={() => preparer(cinquante)}>Préparer 50 fiches</Bouton>
                  <Bouton type="secondaire" disabled={occupe || cible.nb_adresses === 0} data-testid="offre-preparer-tous" onClick={() => preparer(undefined)}>Préparer toutes les fiches</Bouton>
                </div>
              </>
            )}
            {bilan && <p style={{ ...muted, margin: "8px 0 0" }} data-testid="offre-bilan">{bilan}</p>}
          </section>

          <OffreAidesLot onChange={() => charger(campagne.id)} />

          <section style={carte} data-testid="offre-relances" aria-label="Relance">
            <h2 style={titre}>Relance à dix jours</h2>
            <p style={{ ...muted, margin: "0 0 8px" }}>Une seule relance par offre, sans réponse, dix jours après l'envoi. Elle arrive dans le Bureau en carte à valider ; rien ne part sans votre clic. {compteurs ? `${compteurs.relances_a_valider} à valider, ${compteurs.relances_envoyees} envoyée(s).` : ""}</p>
            <Bouton type="secondaire" disabled={occupe} data-testid="offre-relancer" onClick={relancer}>Préparer les relances</Bouton>
          </section>
        </>
      )}

      {campagne && !creation && (
        <section style={carte} data-testid="offre-reponses" aria-label="Réponses à l'offre">
          <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: 8 }}>
            <h2 style={{ ...titre, margin: 0 }}>Réponses à l'offre</h2>
            <label className="flex items-center gap-2" style={{ fontSize: 14, color: "var(--oh-text)" }}>
              <span>Afficher</span>
              <select data-testid="offre-filtre" value={filtre} onChange={(e) => setFiltre(e.target.value)}
                style={{ height: 36, borderRadius: "var(--oh-radius-control)", border: "1px solid var(--oh-border)", background: "var(--oh-bg-surface)", color: "var(--oh-text)", padding: "0 var(--oh-space-3)" }}>
                <option value="">Toutes</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </label>
          </div>
          {affichees.length === 0 ? <p style={muted} data-testid="offre-reponses-vide">Aucune réponse pour l'instant.</p> : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "var(--oh-space-3)" }}>
              {affichees.map((r) => (
                <li key={r.id} data-testid={`offre-reponse-${r.id}`} style={{ borderTop: "0.5px solid var(--oh-border)", paddingTop: 8 }}>
                  <div className="flex flex-wrap items-center gap-2">
                    <strong style={{ fontWeight: 500, color: "var(--oh-text)" }} data-testid="offre-reponse-classe">{r.libelle}</strong>
                    <span style={muted}>{date(r.recue_le)}</span>
                    <select aria-label="Changer le classement" data-testid="offre-requalifier" value={r.classe} disabled={occupe} onChange={(e) => requalifier(r.id, e.target.value)}
                      style={{ height: 32, borderRadius: "var(--oh-radius-control)", border: "1px solid var(--oh-border)", background: "var(--oh-bg-surface)", color: "var(--oh-text)", padding: "0 var(--oh-space-2)" }}>
                      {classes.map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
                    </select>
                  </div>
                  {r.extrait && <p style={{ ...muted, margin: "4px 0 0", overflowWrap: "anywhere" }}>« {r.extrait.slice(0, 200)} »</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {valide && cible?.nb_adresses === 0 && compteurs?.pretes === 0 && compteurs?.proposees === 0 && (
        <OhStatusNote variant="info" data-testid="offre-personne" title="Personne à qui écrire">Aucune adresse ne peut recevoir l'offre pour l'instant.</OhStatusNote>
      )}
    </div>
  );
}
